/*
  product-comparison.js
  Section: product-comparison

  Two independent features — both scoped to [data-pc-card] elements so they
  work when multiple instances of the section appear on a single page.

  1. BIO READ-MORE TOGGLE (ported verbatim from sections/team.liquid IIFE)
     Cards opt in by wrapping bio + toggle in [data-pc-bio-root].
     - Measures the bio on load; hides the toggle if the full text already fits.
     - Click toggles `is-expanded` on the root. CSS lifts the line-clamp.
     - Button label + aria-expanded swap. Chevron rotates via CSS.
     - Idempotent: re-runs on shopify:section:load.

  2. VARIANT SELECT → PRICE + ATC WIRING
     Per-card [data-pc-variant-select] drives:
     - Live price/compare-at update (same formatMoney helper as featured-product-price.liquid)
     - Hidden ATC form id input update so the right variant is added to cart
     - Button disabled state + label ("Sold Out") when variant is unavailable
     - Discount tag recalculated on each variant change
     Reads the JSON from [data-pc-variants] for the variant array (id, price,
     compare_at_price, available). Does NOT dispatch a custom event — this is
     per-card, not page-level, so event coordination is unnecessary.
*/

(function () {
  'use strict';

  /* ── Shared helpers ──────────────────────────────────────────────────── */

  function formatMoney(cents) {
    var n = Number(cents) / 100;
    if (Number.isNaN(n)) return '';
    var rounded = Math.round(n * 100) / 100;
    var hasCents = rounded % 1 !== 0;
    return '$' + (hasCents ? rounded.toFixed(2) : rounded.toFixed(0));
  }

  function calcDiscountLabel(variant) {
    if (!variant.compare_at_price || variant.compare_at_price <= variant.price) return '';
    var pct = Math.round(
      ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
    );
    return pct + '% OFF';
  }

  /* ── 1. Bio read-more toggle ─────────────────────────────────────────── */

  function initBio(card) {
    var roots = card.querySelectorAll('[data-pc-bio-root]');
    roots.forEach(function (root) {
      var cardId = root.getAttribute('data-pc-bio-root');
      if (root.dataset.pcBioInit === 'true') return;
      root.dataset.pcBioInit = 'true';

      var btn = card.querySelector('[data-pc-bio-toggle="' + cardId + '"]');
      var bio = card.querySelector('[data-pc-bio="' + cardId + '"]');
      if (!btn || !bio) return;

      var labelEl = btn.querySelector('[data-pc-bio-toggle-label]') || btn;
      var labelMore = btn.getAttribute('data-label-more') || 'Read more';
      var labelLess = btn.getAttribute('data-label-less') || 'Read less';

      function hideIfFits() {
        if (root.classList.contains('is-expanded')) return;
        if (bio.scrollHeight <= bio.clientHeight + 1) {
          btn.hidden = true;
        } else {
          btn.hidden = false;
        }
      }

      requestAnimationFrame(hideIfFits);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(hideIfFits);
      }
      if (typeof ResizeObserver !== 'undefined') {
        var ro = new ResizeObserver(hideIfFits);
        ro.observe(bio);
      }

      btn.addEventListener('click', function () {
        var expanded = root.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        labelEl.textContent = expanded ? labelLess : labelMore;
      });

      var resizeTimer = null;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(hideIfFits, 150);
      });
    });
  }

  /* ── 2. Variant select → price + ATC wiring ─────────────────────────── */

  function initVariantWiring(card) {
    var cardId = card.getAttribute('data-pc-card');
    if (!cardId) return;
    if (card.dataset.pcVariantInit === 'true') return;
    card.dataset.pcVariantInit = 'true';

    /* Read variant data embedded by the snippet */
    var dataEl = card.querySelector('[data-pc-variants="' + cardId + '"]');
    if (!dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch {
      return;
    }
    if (!variants || !variants.length) return;

    var selectEl    = card.querySelector('[data-pc-variant-select="' + cardId + '"]');
    var priceEl     = card.querySelector('[data-pc-price-current="' + cardId + '"]');
    var compareEl   = card.querySelector('[data-pc-price-compare="' + cardId + '"]');
    var tagEl       = card.querySelector('[data-pc-price-tag="' + cardId + '"]');
    var atcInput    = card.querySelector('[data-pc-atc-input="' + cardId + '"]');
    var atcBtn      = card.querySelector('[data-pc-atc-btn="' + cardId + '"]');
    var atcLabel    = card.querySelector('[data-pc-atc-label="' + cardId + '"]');
    var defaultLabel = atcLabel ? atcLabel.textContent.trim() : 'Add to Cart';

    function applyVariant(variant) {
      if (!variant) return;

      /* Price */
      if (priceEl) {
        priceEl.textContent = formatMoney(variant.price);
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceEl.classList.add('is-on-sale');
        } else {
          priceEl.classList.remove('is-on-sale');
        }
      }

      /* Compare-at */
      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (compareEl) {
        if (onSale) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }

      /* Discount tag */
      if (tagEl) {
        var discountText = onSale ? calcDiscountLabel(variant) : '';
        if (discountText) {
          tagEl.textContent = discountText;
          tagEl.hidden = false;
        } else {
          tagEl.textContent = '';
          tagEl.hidden = true;
        }
      }

      /* ATC form id input */
      if (atcInput) atcInput.value = variant.id;

      /* ATC button state */
      if (atcBtn) atcBtn.disabled = !variant.available;
      if (atcLabel) {
        atcLabel.textContent = variant.available ? defaultLabel : 'Sold Out';
      }
    }

    if (selectEl) {
      selectEl.addEventListener('change', function () {
        var variantId = parseInt(selectEl.value, 10);
        var variant = variants.find(function (v) { return v.id === variantId; });
        applyVariant(variant);
      });
    }
  }

  /* ── Init all cards in a scope ───────────────────────────────────────── */

  function initAll(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-pc-card]').forEach(function (card) {
      initBio(card);
      initVariantWiring(card);
    });
  }

  /* ── Boot ────────────────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* Theme editor re-init on section load */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

})();
